import useServerPagination from '../../components/ui/useServerPagination';
import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import TableActions from '../../components/ui/TableActions';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";

const API_URL = "/api";

const emptyRole = {
  name: "",
  description: "",
  permissions: [],
};

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleForm, setRoleForm] = useState(emptyRole);
  const [permissionSearch,setPermissionSearch]=useState('');

  const server=useServerPagination({endpoint:'/roles',onData:rows=>{setRoles(rows);setLoading(false);}});
  const fetchData = async () => {server.reload();try{const response=await fetch(API_URL+'/permissions',{credentials:'include'});const data=await response.json();if(data.status==='success')setPermissions(data.data||[]);}catch(error){alert(error.message);}finally{setLoading(false);}};

  useEffect(() => {
    fetchData();
  }, []);

  const groupedPermissions = useMemo(() => {
    const grouped = permissions.reduce((result, permission) => {
      const module = permission.module?.trim() || "Other";

      if (!result[module]) {
        result[module] = [];
      }

      result[module].push(permission);
      return result;
    }, {});

    Object.values(grouped).forEach((items) => {
      items.sort((a, b) =>
        (a.display_name || "").localeCompare(b.display_name || ""),
      );
    });

    return Object.fromEntries(
      Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)),
    );
  }, [permissions]);

  const openCreateModal = () => {
    setEditingRoleId(null);
    setRoleForm(emptyRole);
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRoleId(role.id);
    setRoleForm({
      name: role.name || "",
      description: role.description || "",
      permissions: (role.permissions || []).map(Number),
    });
    setShowModal(true);
  };

  const togglePermission = (permissionId) => {
    setRoleForm((previous) => ({
      ...previous,
      permissions: previous.permissions.includes(permissionId)
        ? previous.permissions.filter((id) => id !== permissionId)
        : [...previous.permissions, permissionId],
    }));
  };

  const toggleModule = (modulePermissions) => {
    const permissionIds = modulePermissions.map((permission) => permission.id);

    const allSelected = permissionIds.every((id) =>
      roleForm.permissions.includes(id),
    );

    setRoleForm((previous) => ({
      ...previous,
      permissions: allSelected
        ? previous.permissions.filter((id) => !permissionIds.includes(id))
        : [...new Set([...previous.permissions, ...permissionIds])],
    }));
  };

  const toggleAllPermissions = () => {
    const allIds = permissions.map((permission) => permission.id);
    const allSelected =
      allIds.length > 0 &&
      allIds.every((permissionId) =>
        roleForm.permissions.includes(permissionId),
      );

    setRoleForm((previous) => ({
      ...previous,
      permissions: allSelected ? [] : allIds,
    }));
  };

  const saveRole = async () => {
    if (!roleForm.name.trim()) {
      alert("Role name is required");
      return;
    }

    setSaving(true);

    try {
      const isEditing = Boolean(editingRoleId);

      const response = await fetch(
        isEditing
          ? `${API_URL}/roles/${editingRoleId}`
          : `${API_URL}/roles`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            name: roleForm.name.trim(),
            description: roleForm.description.trim(),
            permissions: roleForm.permissions,
          }),
        },
      );

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not save role");
        return;
      }

      setShowModal(false);
      setEditingRoleId(null);
      setRoleForm(emptyRole);

      await fetchData();
    } catch (error) {
      console.error(error);
      alert("Could not save role");
    } finally {
      setSaving(false);
    }
  };

  const deleteRole = async (role) => {
    if (!window.confirm(`Delete the "${role.name}" role?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/roles/${role.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();

      if (data.status !== "success") {
        alert(data.message || "Could not delete role");
        return;
      }

      await fetchData();
    } catch (error) {
      console.error(error);
      alert("Could not delete role");
    }
  };

  const selectedCount = roleForm.permissions.length;
  const allSelected =
    permissions.length > 0 && selectedCount === permissions.length;

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-serif">Roles & Permissions</h1>
            <p className="mt-1 text-sm text-gray-400">
              Create roles and define what each staff member can access.
            </p>
          </div>

          <PermissionButton permissionModule="roles"
            onClick={openCreateModal}
            className="rounded-xl bg-amber-400 px-5 py-2 font-medium text-black transition hover:bg-amber-300"
          >
            + Add Role
          </PermissionButton>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <PaginatedTable pagination={server.pagination} className="w-full text-sm">
            <thead className="bg-black/40 text-gray-400">
              <tr>
                <th className="px-6 py-4 text-left">Role</th>
                <th className="px-6 py-4 text-left">Description</th>
                <th className="px-6 py-4 text-center">Permissions</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                    Loading roles...
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-10 text-center text-gray-500">
                    No roles created yet. Click “Add Role” to create one.
                  </td>
                </tr>
              ) : (
                roles.map((role) => (
                  <tr
                    key={role.id}
                    className="border-t border-white/10 transition hover:bg-white/5"
                  >
                    <td className="px-6 py-4 font-medium text-white">
                      {role.name}
                    </td>

                    <td className="px-6 py-4 text-gray-400">
                      {role.description || "-"}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className="rounded-full bg-amber-400/10 px-3 py-1 text-xs text-amber-400">
                        {(role.permissions || []).length} selected
                      </span>
                    </td>

                    <td className="px-6 py-4"><TableActions>
                      <div className="flex justify-end gap-2">
                        <PermissionButton permissionModule="roles"
                          onClick={() => openEditModal(role)}
                          className="rounded-lg border border-white/20 px-3 py-1.5 text-xs text-gray-300 transition hover:bg-white/10 hover:text-white"
                        >
                          Edit
                        </PermissionButton>

                        <PermissionButton permissionModule="roles" permissionAction="delete"
                          onClick={() => deleteRole(role)}
                          className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition hover:bg-red-500/10"
                        >
                          Delete
                        </PermissionButton>
                      </div>
                    </TableActions></td>
                  </tr>
                ))
              )}
            </tbody>
          </PaginatedTable>
        </div>

        {showModal && (
          <Modal
            onClose={() => {
              if (!saving) setShowModal(false);
            }}
          >
            <div className="mb-6 flex items-start justify-between pr-8">
              <div>
                <h2 className="text-xl font-serif">
                  {editingRoleId ? "Edit Role" : "Add New Role"}
                </h2>
                <p className="mt-1 text-sm text-gray-400">
                  Select the permissions this role should receive by default.
                </p>
              </div>

              <span className="rounded-full bg-amber-400/10 px-3 py-1 text-xs text-amber-400">
                {selectedCount} permissions
              </span>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-gray-400">
                  Role Name *
                </label>
                <input
                  placeholder="Example: Store Manager"
                  value={roleForm.name}
                  onChange={(event) =>
                    setRoleForm((previous) => ({
                      ...previous,
                      name: event.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2 outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs uppercase tracking-widest text-gray-400">
                  Description
                </label>
                <input
                  placeholder="Example: Manages one store and its staff"
                  value={roleForm.description}
                  onChange={(event) =>
                    setRoleForm((previous) => ({
                      ...previous,
                      description: event.target.value,
                    }))
                  }
                  className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-2 outline-none focus:border-amber-400"
                />
              </div>
            </div>

            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-gray-400">
                Permissions
              </p>

              <PermissionButton permissionModule="roles"
                type="button"
                onClick={toggleAllPermissions}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs transition hover:bg-white/20"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </PermissionButton>
            </div>

            <p className="mb-3 text-sm text-gray-400">Start with View, then add only required actions. Print/export and approvals are separate. Legacy “Manage” permissions grant broad control; do not select them for view-only staff. Branch and department assignments still apply.</p>
            <input aria-label="Search role permissions" placeholder="Search module or action, e.g. inventory edit, print, approve…" value={permissionSearch} onChange={e=>setPermissionSearch(e.target.value)} className="mb-4 w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3" />
            <div className="max-h-[48vh] space-y-5 overflow-y-auto rounded-xl border border-white/10 bg-black/20 p-4">
              {Object.entries(groupedPermissions).map(([module,rows])=>[module,rows.filter(p=>permissionSearch.toLowerCase().trim().split(/\s+/).every(term=>`${module} ${p.display_name} ${p.permission_key} ${p.description||''}`.toLowerCase().includes(term)))]).filter(([,rows])=>rows.length).map(
                ([module, modulePermissions]) => {
                  const moduleIds = modulePermissions.map(
                    (permission) => permission.id,
                  );

                  const moduleAllSelected = moduleIds.every((id) =>
                    roleForm.permissions.includes(id),
                  );

                  return (
                    <div
                      key={module}
                      className="rounded-xl border border-white/10 bg-white/5 p-4"
                    >
                      <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
                        <h3 className="font-semibold text-amber-400">{module}</h3>
                        <PermissionButton permissionModule="roles" type="button" onClick={()=>{const rows=groupedPermissions[module];const ids=new Set(rows.map(p=>p.id));const view=rows.filter(p=>p.permission_key.endsWith('.view')).map(p=>p.id);setRoleForm(prev=>({...prev,permissions:[...prev.permissions.filter(id=>!ids.has(id)),...view]}));}} className="ml-auto mr-3 rounded-lg border border-amber-400/20 px-3 py-1 text-xs text-amber-300">Set module to view only</PermissionButton>

                        <PermissionButton permissionModule="roles"
                          type="button"
                          onClick={() => toggleModule(modulePermissions)}
                          className="rounded-lg bg-white/10 px-3 py-1 text-xs transition hover:bg-white/20"
                        >
                          {moduleAllSelected ? "Deselect Module" : "Select Module"}
                        </PermissionButton>
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {modulePermissions.map((permission) => (
                          <label
                            key={permission.id}
                            className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-black/30 p-3 transition hover:border-amber-400/40 hover:bg-white/5"
                          >
                            <input
                              type="checkbox"
                              checked={roleForm.permissions.includes(
                                permission.id,
                              )}
                              onChange={() => togglePermission(permission.id)}
                              className="mt-1 accent-amber-400"
                            />

                            <div>
                              <p className="font-medium text-white">
                                {permission.display_name}
                              </p>
                              <p className="text-[11px] text-gray-500">
                                {permission.permission_key}
                              </p>
                              {permission.description&&<p className="mt-1 text-xs text-gray-400">{permission.description}</p>}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                },
              )}
            </div>

            <div className="mt-8 flex justify-end gap-3">
              <PermissionButton permissionModule="roles"
                type="button"
                disabled={saving}
                onClick={() => setShowModal(false)}
                className="rounded-xl border border-white/20 px-6 py-2 text-sm transition hover:bg-white/10"
              >
                Cancel
              </PermissionButton>

              <PermissionButton permissionModule="roles"
                type="button"
                disabled={saving}
                onClick={saveRole}
                className="rounded-xl bg-amber-400 px-6 py-2 font-medium text-black transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving
                  ? "Saving..."
                  : editingRoleId
                    ? "Save Changes"
                    : "Create Role"}
              </PermissionButton>
            </div>
          </Modal>
        )}
      </div>
    </DashboardLayout>
  );
}

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm">
      <div className="relative max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0b0f] p-8">
        <PermissionButton permissionModule="roles"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 transition hover:text-white"
          aria-label="Close role modal"
        >
          ✕
        </PermissionButton>

        {children}
      </div>
    </div>
  );
}
